import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef,
  type MRT_PaginationState,
  type MRT_RowData,
  type MRT_SortingState,
} from 'mantine-react-table';

/**
 * MAFA-flavoured DataGrid built on top of mantine-react-table v2. The
 * wrapper centralises the visual + behavioural defaults so each page
 * only declares its columns:
 *
 *   • column sorting on header click
 *   • column resizing
 *   • column filters (text + global search)
 *   • sticky header on scroll
 *   • compact density (matches the MAFA design — comfortable wastes
 *     vertical space on operator workstations)
 *   • row hover + click navigation (via onRowClick prop)
 *   • optional server-side pagination (pass pagination + onPaginationChange
 *     + rowCount; defaults to client-side)
 *
 * The visual integration with the MAFA tokens lives in
 * src/theme/datagrid.css — we override only the strictly necessary
 * pieces (border colours, header weight, font, hover tint).
 */

export type { MRT_ColumnDef as ColumnDef, MRT_SortingState as SortingState } from 'mantine-react-table';

type Props<T extends MRT_RowData> = {
  columns: MRT_ColumnDef<T>[];
  data: T[];
  isLoading?: boolean;

  /** Server-side pagination — leave undefined for client-side. */
  pagination?: MRT_PaginationState;
  onPaginationChange?: (next: MRT_PaginationState) => void;
  rowCount?: number;

  /**
   * Server-side sorting — leave undefined for client-side. On a
   * server-paginated grid, client-side sorting would only reorder the rows of
   * the page currently loaded, which reads as a bug to the operator: pass
   * these two together with `pagination` whenever the data is paginated.
   */
  sorting?: MRT_SortingState;
  onSortingChange?: (next: MRT_SortingState) => void;

  /** Called when a row body (not its filter / sort UI) is clicked. */
  onRowClick?: (row: T) => void;

  /**
   * Hauteur maximale du corps, pour que l'en-tête reste en haut.
   *
   * Par défaut, la grille prend la place DISPONIBLE au lieu d'un plafond fixe :
   * un plafond en dur ne tient pas compte de ce qui est empilé au-dessus
   * — barres de filtres, bandeaux — ni du niveau de zoom. Combinés, ils
   * repoussaient les lignes hors de l'écran, ce qui donnait une grille
   * apparemment vide alors qu'elle contenait ses lignes.
   */
  maxHeight?: number | string;

  /** Empty-state copy when no rows are returned. */
  emptyMessage?: string;

  /** Disable the "filters" row (per-column inputs). */
  disableColumnFilters?: boolean;

  /** Disable the global search box in the toolbar. */
  disableGlobalFilter?: boolean;
};

const DEFAULT_PAGE_SIZES = [25, 50, 100];

export function DataGrid<T extends MRT_RowData>({
  columns,
  data,
  isLoading,
  pagination,
  onPaginationChange,
  rowCount,
  sorting,
  onSortingChange,
  onRowClick,
  maxHeight: maxHeightProp,
  emptyMessage = 'Aucune ligne à afficher.',
  disableColumnFilters,
  disableGlobalFilter,
}: Props<T>) {
  // Hauteur MESURÉE plutôt que devinée.
  //
  // Un plafond en dur (560 px) ou même un calcul approximatif ignore ce qui est
  // empilé au-dessus — barres de filtres, bandeaux, en-tête de page — et le
  // niveau de zoom. Quand le total dépassait la fenêtre, la page se mettait à
  // défiler EN PLUS de la grille : deux défilements imbriqués, et des lignes
  // qui sortaient de l'écran alors que la pagination restait visible. D'où une
  // grille en apparence vide.
  //
  // On mesure donc la place réellement disponible sous le haut de la grille, et
  // on s'y ajuste. Résultat : la page ne défile plus, seule la grille le fait,
  // et l'en-tête reste en haut.
  const shellRef = useRef<HTMLDivElement>(null);
  const [available, setAvailable] = useState<number | null>(null);

  const measure = useCallback(() => {
    const el = shellRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    // Marge basse : la barre de pagination de la grille + une respiration.
    const next = Math.max(220, Math.round(window.innerHeight - top - 96));
    setAvailable((prev) => (prev != null && Math.abs(prev - next) < 8 ? prev : next));
  }, []);

  useLayoutEffect(measure, [measure]);
  useEffect(() => {
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  const serverPagination = pagination != null && onPaginationChange != null;
  const serverSorting = sorting != null && onSortingChange != null;

  // Stable references so MRT doesn't re-create its internal table on every render.
  const memoCols = useMemo(() => columns, [columns]);

  const showTopToolbar = !disableGlobalFilter || !disableColumnFilters;

  const table = useMantineReactTable({
    columns: memoCols,
    data,
    layoutMode: 'grid',
    enableColumnResizing: true,
    columnResizeMode: 'onEnd',
    enableStickyHeader: true,
    enableColumnFilters: !disableColumnFilters,
    enableGlobalFilter: !disableGlobalFilter,
    enableColumnActions: false,
    enableFullScreenToggle: false,
    enableDensityToggle: false,
    enableHiding: false,
    enableTopToolbar: showTopToolbar,
    enableBottomToolbar: true,
    enablePagination: true,
    enableSortingRemoval: true,
    positionGlobalFilter: 'left',
    initialState: {
      density: 'xs',
      showColumnFilters: false,
      pagination: { pageIndex: 0, pageSize: DEFAULT_PAGE_SIZES[1] },
    },
    state: {
      isLoading,
      ...(serverPagination ? { pagination } : {}),
      ...(serverSorting ? { sorting } : {}),
    },
    manualPagination: serverPagination,
    manualSorting: serverSorting,
    onSortingChange: serverSorting
      ? (updater) => {
          const next =
            typeof updater === 'function'
              ? (updater as (prev: MRT_SortingState) => MRT_SortingState)(sorting!)
              : updater;
          onSortingChange?.(next);
        }
      : undefined,
    rowCount: serverPagination ? rowCount ?? data.length : undefined,
    onPaginationChange: serverPagination
      ? (updater) => {
          const next =
            typeof updater === 'function'
              ? (updater as (prev: MRT_PaginationState) => MRT_PaginationState)(pagination!)
              : updater;
          onPaginationChange?.(next);
        }
      : undefined,
    paginationDisplayMode: 'pages',
    mantinePaginationProps: {
      withEdges: false,
      showRowsPerPage: true,
      rowsPerPageOptions: DEFAULT_PAGE_SIZES.map(String),
    },
    mantineTableContainerProps: {
      // maxHeight explicite fourni par l'appelant → il gagne ; sinon la place
      // mesurée ; en tout dernier recours, la valeur par défaut.
      style: { maxHeight: maxHeightProp ?? available ?? 560, overflowX: 'auto' },
    },
    mantineTableProps: {
      className: 'mafa-datagrid',
      striped: false,
      withTableBorder: false,
      highlightOnHover: true,
    },
    mantineTableHeadCellProps: {
      style: {
        fontFamily: 'var(--ff)',
        fontWeight: 700,
        fontSize: 11,
        letterSpacing: '.04em',
        textTransform: 'uppercase',
        color: 'var(--ink-3)',
        background: 'var(--panel)',
        borderBottom: '1px solid color-mix(in oklch, var(--line) 55%, transparent)',
      },
    },
    mantineTableBodyCellProps: {
      style: {
        fontFamily: 'var(--ff)',
        fontSize: 13,
        color: 'var(--ink-2)',
        // Row separators are drawn by Mantine via --table-border-color
        // (see datagrid.css). Setting a borderBottom here too would double it.
      },
    },
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: onRowClick ? () => onRowClick(row.original) : undefined,
      style: onRowClick ? { cursor: 'pointer' } : undefined,
    }),
    mantineTopToolbarProps: {
      style: {
        background: 'var(--panel)',
        padding: '10px 14px',
      },
    },
    mantineBottomToolbarProps: {
      style: {
        background: 'var(--panel)',
        padding: '12px 16px',
        minHeight: 56,
        overflow: 'visible',
      },
    },
    mantineFilterTextInputProps: {
      variant: 'unstyled',
      placeholder: 'Filtrer…',
      size: 'xs',
    },
    mantineSearchTextInputProps: {
      placeholder: 'Rechercher dans la table',
      size: 'xs',
    },
    localization: FR_LOCALIZATION,
    renderEmptyRowsFallback: () => (
      <div
        style={{
          padding: '38px 24px',
          textAlign: 'center',
          color: 'var(--ink-3)',
          fontWeight: 600,
          fontSize: 13.5,
        }}
      >
        {emptyMessage}
      </div>
    ),
  });

  return (
    <div ref={shellRef}>
      <MantineReactTable table={table} />
    </div>
  );
}

const FR_LOCALIZATION = {
  actions: 'Actions',
  and: 'et',
  cancel: 'Annuler',
  changeFilterMode: 'Changer le mode de filtre',
  changeSearchMode: 'Changer le mode de recherche',
  clearFilter: 'Effacer le filtre',
  clearSearch: 'Effacer la recherche',
  clearSort: 'Effacer le tri',
  collapse: 'Replier',
  collapseAll: 'Tout replier',
  columnActions: 'Actions sur la colonne',
  copiedToClipboard: 'Copié',
  dropToGroupBy: 'Glisser pour grouper par',
  edit: 'Éditer',
  expand: 'Déplier',
  expandAll: 'Tout déplier',
  filterByColumn: 'Filtrer la colonne',
  filterMode: 'Mode de filtre',
  goToFirstPage: "Aller à la première page",
  goToLastPage: "Aller à la dernière page",
  goToNextPage: 'Page suivante',
  goToPreviousPage: 'Page précédente',
  grab: 'Saisir',
  groupedBy: 'Groupé par',
  groupByColumn: 'Grouper par la colonne',
  hideAll: 'Tout cacher',
  hideColumn: 'Cacher la colonne',
  max: 'Max',
  min: 'Min',
  move: 'Déplacer',
  noRecordsToDisplay: 'Aucune ligne',
  noResultsFound: 'Aucun résultat',
  of: 'sur',
  or: 'ou',
  pageNumber: 'Page #',
  pinToLeft: 'Épingler à gauche',
  pinToRight: 'Épingler à droite',
  resetColumnSize: 'Réinitialiser la largeur',
  resetOrder: 'Réinitialiser l\'ordre',
  rowActions: 'Actions',
  rowNumber: '#',
  rowNumbers: 'Numéros',
  rowsPerPage: 'Lignes par page',
  save: 'Enregistrer',
  search: 'Rechercher',
  selectedCountOfRowCountRowsSelected: '{selectedCount} sur {rowCount} lignes sélectionnées',
  showAll: 'Tout afficher',
  showAllColumns: 'Toutes les colonnes',
  showHideFilters: 'Filtres',
  showHideSearch: 'Rechercher',
  sortByColumnAsc: 'Trier {column} croissant',
  sortByColumnDesc: 'Trier {column} décroissant',
  sortedByColumnAsc: 'Trié par {column} ↑',
  sortedByColumnDesc: 'Trié par {column} ↓',
  thenBy: ', puis par',
  toggleDensity: 'Densité',
  toggleFullScreen: 'Plein écran',
  toggleSelectAll: 'Tout sélectionner',
  toggleSelectRow: 'Sélectionner la ligne',
  toggleVisibility: 'Visibilité',
  ungroupByColumn: 'Dégrouper',
  unpin: 'Détacher',
  unpinAll: 'Tout détacher',
};
